import React, { createContext, useContext, useEffect, useState } from 'react';
import type { SiteChatAttachment, SiteChatUpload } from '../types';
import type { SiteChatUi } from '../ui';
import { MediaIcon, VoicePlayer } from './media';

/**
 * Attachments and the composer's upload list, drawn after the in-app chat's `accel-framework/components/Files`
 * (`FileListItem`, `FilePreview`, `FileProgress`, `FileIcon`): a small square preview (the picture itself, or a
 * page with the coloured extension label), a ring of progress over it while it uploads, retry on failure, the name
 * with its extension and the size. Pictures open in a viewer, video and audio play in the bubble.
 */

const ICON = {
    x: 'M6 6l12 12M18 6L6 18',
    retry: 'M4 12a8 8 0 0 1 14-5.3M20 4v5h-5M20 12a8 8 0 0 1-14 5.3M4 20v-5h5',
    download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
    left: 'M15 5l-7 7 7 7',
    right: 'M9 5l7 7-7 7',
    play: 'M8 5.5v13l10.5-6.5z',
};

export function formatSize(size: number) {
    if (size >= 1024 * 1024)
        return `${(size / 1024 / 1024).toFixed(1)} MB`;
    if (size >= 1024)
        return `${Math.round(size / 1024)} KB`;
    return `${size} B`;
}

export function extensionOf(file: { extension?: string | null; name?: string | null }) {
    const raw = (file.extension ?? '').replace(/^\./, '') || (file.name?.includes('.') ? file.name.split('.').pop() ?? '' : '');
    return raw.toLowerCase();
}

/** The extension label colours of `FileIcon` (`file-icons.scss`). */
function badgeColor(extension: string) {
    if (extension === 'pdf') return '#e13d34';
    if (extension.startsWith('doc') || extension === 'rtf' || extension === 'odt') return '#307cf1';
    if (extension.startsWith('xls') || extension === 'csv' || extension === 'ods') return '#0f9d58';
    if (extension.startsWith('ppt') || extension === 'odp') return '#d24726';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return '#acacac';
    if (extension === 'txt') return '#5eb533';
    if (['mp3', 'wma', 'm4a', 'flac', 'wav', 'ogg', 'opus', 'aac'].includes(extension)) return '#8e44ad';
    if (['mp4', 'wmv', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(extension)) return '#7a3ce7';
    if (['bmp', 'jpg', 'jpeg', 'gif', 'png', 'webp', 'heic'].includes(extension)) return '#f4b400';
    return '#018fef';
}

/** A page with a folded corner and the extension on a coloured label, like `FileIcon`. */
export function FileBadge({ extension, size = 'sm' }: { extension: string; size?: 'xs' | 'sm' }) {
    return (
        <span className={`axl-sc-fbadge ${size}`} aria-hidden="true">
            {extension ? <span className="ext" style={{ background: badgeColor(extension) }}>{extension.slice(0, 4).toUpperCase()}</span> : null}
        </span>
    );
}

/** The ring of `FileProgress`, in SVG. */
function ProgressRing({ percent }: { percent: number }) {
    const r = 13;
    const c = 2 * Math.PI * r;
    return (
        <svg className="axl-sc-ring" width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
            <circle cx="16" cy="16" r={r} fill="none" strokeWidth="3" className="track" />
            <circle cx="16" cy="16" r={r} fill="none" strokeWidth="3" className="bar"
                strokeDasharray={c} strokeDashoffset={c * (1 - Math.max(4, Math.min(100, percent)) / 100)}
                transform="rotate(-90 16 16)" strokeLinecap="round" />
        </svg>
    );
}

// ---------- composer: the upload list ----------

export function UploadItem({ upload, ui, maxFileSizeMb, onRemove, onRetry }: {
    upload: SiteChatUpload;
    ui: SiteChatUi;
    maxFileSizeMb: number;
    onRemove: () => void;
    onRetry: () => void;
}) {
    const error = upload.state === 'too-large' ? ui.fileTooLarge.replace('{size}', String(maxFileSizeMb))
        : upload.state === 'type' ? ui.fileType
            : upload.state === 'error' ? ui.fileFailed
                : null;
    return (
        <div className={`axl-sc-up ${error ? 'err' : ''}`} data-sc="upload" data-state={upload.state}>
            <span className="axl-sc-upp" data-state={upload.state}>
                {upload.kind === 'picture' && upload.previewUrl
                    ? <img src={upload.previewUrl} alt="" />
                    : upload.kind === 'video' && upload.previewUrl
                        ? <video src={upload.previewUrl} muted preload="metadata" />
                        : <FileBadge extension={upload.extension} size="xs" />}
                {upload.state === 'uploading' ? <span className="ov"><ProgressRing percent={upload.progress} /></span> : null}
                {upload.state === 'error'
                    ? <button type="button" className="ov" aria-label={ui.retryUpload} title={ui.retryUpload} onClick={onRetry}><MediaIcon d={ICON.retry} size={18} /></button>
                    : null}
                {upload.state === 'ready' && upload.kind === 'video' ? <span className="play"><MediaIcon d={ICON.play} size={12} fill /></span> : null}
            </span>
            <span className="axl-sc-upi">
                <span className="n" title={upload.name}>{upload.name}</span>
                <span className={error ? 'e' : 's'}>{error ?? (upload.state === 'uploading' ? `${formatSize(upload.size)} · ${upload.progress}%` : formatSize(upload.size))}</span>
            </span>
            <button type="button" className="axl-sc-upx" aria-label={ui.removeFile} title={ui.removeFile} onClick={onRemove}>
                <MediaIcon d={ICON.x} size={14} />
            </button>
        </div>
    );
}

// ---------- bubbles: the attachments of a message ----------

export type ViewerItem = { url: string; name: string };
type ViewerApi = { open(items: ViewerItem[], index: number): void };
export const ViewerContext = createContext<ViewerApi>({ open: () => undefined });

/** The attachment drawn as the bubble's banner: a picture or a video that can be shown. */
export function isBannerFile(file: SiteChatAttachment) {
    return !file.voice && !!file.url && (file.type === 'picture' || file.type === 'video');
}

/**
 * Only the first picture or video is a banner across the bubble; every other file, pictures and video included,
 * follows as an ordinary file row.
 */
export function Attachments({ files, ui, safeUrl }: { files: SiteChatAttachment[]; ui: SiteChatUi; safeUrl: (url: string) => string }) {
    const viewer = useContext(ViewerContext);
    const bannerIndex = files.findIndex(isBannerFile);
    const banner = bannerIndex >= 0 ? files[bannerIndex] : null;
    const rest = files.filter((_, index) => index !== bannerIndex);
    const picture = banner?.type === 'picture' ? { url: safeUrl(banner.url), name: `${banner.name}${banner.extension ?? ''}` } : null;
    return (
        <>
            {picture ? (
                <div className="axl-sc-pics">
                    <button type="button" className="axl-sc-pic" aria-label={ui.openPicture} onClick={() => viewer.open([picture], 0)}>
                        <img src={picture.url} alt={picture.name} loading="lazy" />
                    </button>
                </div>
            ) : banner ? <Attachment file={banner} ui={ui} safeUrl={safeUrl} /> : null}
            {rest.map(file => <Attachment key={file.id} file={file} ui={ui} safeUrl={safeUrl} row={file.type === 'picture' || file.type === 'video'} />)}
        </>
    );
}

function Attachment({ file, ui, safeUrl, row = false }: { file: SiteChatAttachment; ui: SiteChatUi; safeUrl: (url: string) => string; row?: boolean }) {
    const url = file.url ? (file.url.startsWith('blob:') ? file.url : safeUrl(file.url)) : '';
    const name = `${file.name}${file.extension ?? ''}`;
    if (row)
        return <FileRow file={file} url={url} name={name} ui={ui} />;
    if (file.voice)
        return <VoicePlayer url={url} duration={file.duration} levels={file.levels} ui={ui} />;
    if (file.type === 'video' && url)
        return (
            <div className="axl-sc-vid">
                <video src={url} controls playsInline preload="metadata" />
            </div>
        );
    if (file.type === 'audio' && url)
        return (
            <div className="axl-sc-aud">
                <span className="n" title={name}>{name}</span>
                <VoicePlayer url={url} duration={file.duration} levels={file.levels} ui={ui} />
            </div>
        );
    return <FileRow file={file} url={url} name={name} ui={ui} />;
}

/** The document card of `FileListItem`: the page icon, name with extension, size, download. */
function FileRow({ file, url, name, ui }: { file: SiteChatAttachment; url: string; name: string; ui: SiteChatUi }) {
    return (
        <a className="axl-sc-fc" href={url || undefined} target="_blank" rel="noopener noreferrer" download={name} title={name}>
            <FileBadge extension={extensionOf(file)} />
            <span className="axl-sc-fn">
                <span className="n">{name}</span>
                <span className="s">{formatSize(file.size)}</span>
            </span>
            <span className="axl-sc-fd" aria-label={ui.download}><MediaIcon d={ICON.download} size={16} /></span>
        </a>
    );
}

// ---------- the picture viewer ----------

export function Lightbox({ items, index, ui, onClose }: { items: ViewerItem[]; index: number; ui: SiteChatUi; onClose: () => void }) {
    const [current, setCurrent] = useState(index);
    const item = items[current];
    const many = items.length > 1;

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape')
                onClose();
            if (e.key === 'ArrowRight' && many)
                setCurrent(value => (value + 1) % items.length);
            if (e.key === 'ArrowLeft' && many)
                setCurrent(value => (value - 1 + items.length) % items.length);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [items.length, many, onClose]);

    if (!item)
        return null;
    return (
        <div className="axl-sc-lb" role="dialog" aria-label={item.name} data-sc="viewer" onClick={onClose}>
            <div className="axl-sc-lbt" onClick={e => e.stopPropagation()}>
                <span className="n">{item.name}{many ? ` · ${current + 1} / ${items.length}` : ''}</span>
                <a className="b" href={item.url} target="_blank" rel="noopener noreferrer" download={item.name} aria-label={ui.download} title={ui.download}>
                    <MediaIcon d={ICON.download} size={18} />
                </a>
                <button type="button" className="b" aria-label={ui.close} title={ui.close} onClick={onClose}><MediaIcon d={ICON.x} size={18} /></button>
            </div>
            <img className="axl-sc-lbi" src={item.url} alt={item.name} onClick={e => e.stopPropagation()} />
            {many ? (
                <>
                    <button type="button" className="axl-sc-lbn prev" aria-label={ui.previous}
                        onClick={e => { e.stopPropagation(); setCurrent((current - 1 + items.length) % items.length); }}>
                        <MediaIcon d={ICON.left} size={22} />
                    </button>
                    <button type="button" className="axl-sc-lbn next" aria-label={ui.next}
                        onClick={e => { e.stopPropagation(); setCurrent((current + 1) % items.length); }}>
                        <MediaIcon d={ICON.right} size={22} />
                    </button>
                </>
            ) : null}
        </div>
    );
}
